/**
 * WebView detection and React Native bridge communication utilities.
 *
 * The ecommerce website runs inside a React Native WebView on mobile.
 * These helpers detect the WebView environment and provide a unified
 * API for sending messages to the native shell.
 */

// ---------------------------------------------------------------------------
// Global type augmentation for the React Native WebView bridge
// ---------------------------------------------------------------------------
declare global {
  interface Window {
    /** iOS bridge – injected by React Native WebView on WKWebView */
    webkit?: {
      messageHandlers?: {
        ReactNativeWebView?: {
          postMessage: (data: string) => void;
        };
      };
    };
    /** Android / cross-platform bridge – injected by React Native WebView */
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

// ---------------------------------------------------------------------------
// Detection helpers
// ---------------------------------------------------------------------------

/**
 * Returns `true` when running inside a React Native WebView.
 * Checks both the iOS (webkit.messageHandlers) and Android (window.ReactNativeWebView) bridges.
 */
export function isReactNativeWebView(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    typeof window.ReactNativeWebView?.postMessage === 'function' ||
    typeof window.webkit?.messageHandlers?.ReactNativeWebView?.postMessage === 'function'
  );
}

// ---------------------------------------------------------------------------
// Bridge communication
// ---------------------------------------------------------------------------

/**
 * Sends a JSON-serialised message to the React Native shell.
 *
 * Tries the iOS bridge first (`webkit.messageHandlers.ReactNativeWebView`),
 * then falls back to the Android bridge (`window.ReactNativeWebView`).
 *
 * @returns `true` if a bridge was found and the message was sent.
 */
export function postMessageToNative(data: object): boolean {
  if (typeof window === 'undefined') return false;

  const payload = JSON.stringify(data);

  if (typeof window.webkit?.messageHandlers?.ReactNativeWebView?.postMessage === 'function') {
    window.webkit.messageHandlers.ReactNativeWebView.postMessage(payload);
    return true;
  }

  if (typeof window.ReactNativeWebView?.postMessage === 'function') {
    window.ReactNativeWebView.postMessage(payload);
    return true;
  }

  return false;
}
